import { translateValidationError } from './translation';

export interface ValidationError {
  message: string;
}

export interface UserLimits {
  max_accounts_number: string;
  max_amount_per_transaction: number;
  max_amount_per_week: string;
  max_transfers_per_week: string;
}

/**
 * Validate the empty amount
 * @param amount - Amount to validate
 * @returns Validation error if the amount is empty or zero
 */
export const validateEmptyAmount = (amount: string): ValidationError | null => {
  const numericAmount = parseFloat(amount);

  if (!numericAmount) {
    return { message: translateValidationError('VALIDATION.AMOUNT_IS_REQUIRED') };
  }

  return null;
};

/**
 * Validate the available balance
 * @param amount - Amount to validate
 * @param availableBalance - Available balance
 * @returns Validation error if the available balance is exceeded
 */
export const validateAvailableBalance = (amount: string, availableBalance: string): ValidationError | null => {
  const numericAmount = parseFloat(amount);
  const numericAvailableBalance = parseFloat(availableBalance);

  if (numericAmount > numericAvailableBalance) {
    return { message: translateValidationError('VALIDATION.AVAILABLE_BALANCE') };
  }

  return null;
};

/**
 * Validate the maximum amount per transaction
 * @param amount - Amount to validate
 * @param userLimitsData - User limits data
 * @returns Validation error if the maximum amount per transaction is exceeded
 */
const valideMaxAmountPerTransaction = (amount: string, userLimitsData?: UserLimits): ValidationError | null => {
  const { max_amount_per_transaction: maxAmountPerTransaction = 0 } = userLimitsData || {};
  const numericAmount = parseFloat(amount);

  if (numericAmount > maxAmountPerTransaction) {
    return { message: translateValidationError('VALIDATION.MAXIMUM_TRANSACTION_LIMIT', { maxAmountPerTransaction }) };
  }

  return null;
};

/**
 * Validate the maximum amount per week
 * @param amount - Amount to validate
 * @param userLimitsData - User limits data
 * @returns Validation error if the maximum amount per week is exceeded
 */
const validateMaxAmountPerWeek = (amount: string, userLimitsData?: UserLimits): ValidationError | null => {
  const { max_amount_per_week: maxAmountPerWeek = '0/0' } = userLimitsData || {};
  const [usedAmount, maxAmount] = maxAmountPerWeek.split('/').map((val: any) => parseFloat(val) || 0);
  const numericAmount = parseFloat(amount);

  if (usedAmount + numericAmount > maxAmount) {
    return {
      message: translateValidationError('VALIDATION.MAXIMUM_WEEKLY_AMOUNT_LIMIT', {
        maxAmountPerWeek: maxAmount,
      }),
    };
  }

  return null;
};

/**
 * Validate the maximum transfers per week
 * @param userLimitsData - User limits data
 * @returns Validation error if the maximum transfers per week is exceeded
 */
const validateMaxTransfersPerWeek = (userLimitsData?: UserLimits): ValidationError | null => {
  const { max_transfers_per_week: maxTransfersPerWeek = '0/0' } = userLimitsData || {};
  const [usedTransfers, maxTransfers] = maxTransfersPerWeek.split('/').map((val) => parseInt(val) || 0);

  if (usedTransfers >= maxTransfers) {
    return {
      message: translateValidationError('VALIDATION.MAXIMUM_WEEKLY_TRANSFERS_LIMIT', {
        maxTransfersPerWeek: maxTransfers,
      }),
    };
  }

  return null;
};

export const validateFeatAmount = (
  amount: string,
  availableBalance: string,
  userLimitsData?: UserLimits
): ValidationError | null => {
  const emptyError = validateEmptyAmount(amount);
  if (emptyError) return emptyError;

  const balanceError = validateAvailableBalance(amount, availableBalance);
  if (balanceError) return balanceError;

  const userLimitsError = validateUserLimits(amount, userLimitsData);
  if (userLimitsError) return userLimitsError;

  return null;
};

export const validateCryptoAmount = (amount: string, availableBalance: string): ValidationError | null => {
  const emptyError = validateEmptyAmount(amount);
  if (emptyError) return emptyError;

  const balanceError = validateAvailableBalance(amount, availableBalance);
  if (balanceError) return balanceError;

  return null;
};

/**
 * Validate only user limits without balance check
 * @param amount - Amount to validate
 * @param userLimitsData - User limits data
 * @returns Validation error if any limit is exceeded
 */
export const validateUserLimits = (amount: string, userLimitsData?: UserLimits): ValidationError | null => {
  const maxAmountPerTransactionError = valideMaxAmountPerTransaction(amount, userLimitsData);
  if (maxAmountPerTransactionError) return maxAmountPerTransactionError;

  const maxAmountPerWeekError = validateMaxAmountPerWeek(amount, userLimitsData);
  if (maxAmountPerWeekError) return maxAmountPerWeekError;

  const maxTransfersPerWeekError = validateMaxTransfersPerWeek(userLimitsData);
  if (maxTransfersPerWeekError) return maxTransfersPerWeekError;

  return null;
};

/**
 * Validates an email address
 * @param email - Email address to validate
 * @returns True if email is valid
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};